import { useCallback, useEffect, useState } from "react";
import { kindLabel } from "@/lib/leadKind";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarPlus, Pencil, Trash2, Phone, Building2, Home } from "lucide-react";
import { PmApptDialog } from "@/components/PmApptDialog";

const ICON = { phone: Phone, office: Building2, home: Home };
const LABEL = { phone: "Phone", office: "In-office", home: "In-home" };
const TONE = { scheduled: "bg-emerald-50 text-emerald-800", completed: "bg-slate-100 text-slate-700", canceled: "bg-red-50 text-red-700", no_show: "bg-amber-50 text-amber-800" };
const fmt = (a) => (a.starts_at ? new Date(a.starts_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : a.when_text || "Time not set");

const Row = ({ a, onEdit, onDelete }) => {
  const Icon = ICON[a.type] || Phone;
  return (
    <div className="grid gap-2 border-t border-slate-100 py-3 text-sm md:grid-cols-[1.3fr_2fr_2fr_auto]" data-testid={`pm-appt-${a.id}`}>
      <div><div className="font-semibold text-slate-900" data-testid={`pm-appt-when-${a.id}`}>{fmt(a)}</div>
        <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><Icon className="h-3.5 w-3.5" />{LABEL[a.type] || a.type}{a.source === "ava" ? " · booked by Ava" : ""}</div></div>
      <div><div className="font-medium text-slate-900">{a.name}</div><div className="text-xs text-slate-500">{a.phone || "No phone"}{a.email ? ` · ${a.email}` : ""}</div>
        <div className="text-xs text-slate-500">{kindLabel(a.kind)}</div></div>
      <div className="text-xs text-slate-600"><div>{a.address || "—"}</div>{a.notes && <div className="mt-0.5 line-clamp-2 text-slate-500">{a.notes}</div>}
        {(a.owner_texted || a.pm_called) && <div className="mt-0.5 text-emerald-700" data-testid={`pm-appt-reminders-${a.id}`}>
          {[a.owner_texted && "Owner reminder texted", a.pm_called && "You got a reminder call"].filter(Boolean).join(" · ")}</div>}</div>
      <div className="flex items-start gap-1">
        <Badge className={`border-0 ${TONE[a.status] || ""}`} data-testid={`pm-appt-status-${a.id}`}>{a.status.replace("_", "-")}</Badge>
        <Button size="icon" variant="ghost" onClick={() => onEdit(a)} data-testid={`pm-appt-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => onDelete(a)} data-testid={`pm-appt-delete-${a.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
      </div>
    </div>
  );
};

export default function PmAppointments() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/pm/appointments").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const del = async (a) => {
    if (!window.confirm(`Delete the appointment with ${a.name}?`)) return;
    try { await api.delete(`/pm/appointments/${a.id}`); toast.success("Appointment deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const now = new Date().toISOString();
  const upcoming = rows.filter((a) => a.status === "scheduled" && (!a.starts_at || a.starts_at >= now));
  const past = rows.filter((a) => !upcoming.includes(a)).reverse();
  return (
    <div className="mx-auto max-w-6xl" data-testid="pm-appointments-page">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-3xl font-extrabold text-slate-900">Appointments</h1>
          <p className="text-sm text-slate-600">Phone, in-office and in-home appointments booked by Ava or added by you.</p></div>
        <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="pm-appt-add"><CalendarPlus className="mr-2 h-4 w-4" />Add appointment</Button>
      </div>
      <Card className="mb-5 p-6">
        <h3 className="mb-1 font-display text-lg font-bold text-slate-900">Upcoming ({upcoming.length})</h3>
        {upcoming.map((a) => <Row key={a.id} a={a} onEdit={(x) => { setEdit(x); setOpen(true); }} onDelete={del} />)}
        {!upcoming.length && <p className="py-6 text-center text-sm text-slate-500" data-testid="pm-appt-empty">No upcoming appointments.</p>}
      </Card>
      {past.length > 0 && <Card className="p-6"><h3 className="mb-1 font-display text-lg font-bold text-slate-900">Past &amp; closed ({past.length})</h3>
        {past.map((a) => <Row key={a.id} a={a} onEdit={(x) => { setEdit(x); setOpen(true); }} onDelete={del} />)}</Card>}
      <PmApptDialog open={open} onClose={() => setOpen(false)} appt={edit} onSaved={load} />
    </div>
  );
}
